import { useId } from '$store/sdk/useId.ts'
import { useSignal } from '@preact/signals'
import c from 'site/sdk/c.ts'
import { ComponentChildren } from 'preact'
import { useEffect } from 'preact/hooks'

interface Props {
    onClose?: () => void
    open?: boolean
    class?: string
    style?: string
    children?: ComponentChildren
    loading?: 'eager' | 'lazy'
    id?: string
    ariaLabel?: string
}

function Modal(
    { children, open, onClose, loading = 'lazy', id: _id, ariaLabel, class: _class }: Props,
) {
    const lazy = useSignal(loading === 'lazy' && !open)
    const id = _id ?? useId()

    const ariaLabelId = useId()

    useEffect(() => {
        const handler = (e: KeyboardEvent) =>
            (e.key === 'Escape' || e.keyCode === 27) && open && onClose?.()

        addEventListener('keydown', handler)

        return () => {
            removeEventListener('keydown', handler)
        }
    }, [open])

    useEffect(() => {
        lazy.value = false

        window.addEventListener('pageshow', (e) => {
            const $input = document.getElementById(id) as HTMLInputElement

            $input.checked = !!open
        })
    }, [])

    return (
        <>
            {ariaLabel && (
                <span id={`labelled:${ariaLabelId}`} class='sr-only'>
                    {ariaLabel}
                </span>
            )}
            <input
                id={id}
                checked={open}
                type='checkbox'
                class='modal-toggle peer'
                onChange={(e) => {
                    e.currentTarget.checked === false && onClose?.()
                }}
                aria-labelledby={ariaLabel ? `labelled:${ariaLabelId}` : undefined}
            />
            <div class={c('modal group', _class ?? '', id)}>
                {!lazy.value && children}
                <label class='modal-backdrop' for={id}>
                    Close
                </label>
            </div>
        </>
    )
}

export default Modal
